import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { AUTOMATION_BASE_KEY, AUTOMATION_KEY, AUTOMATIONS_KEY } from '@/utils/queryKeys.ts';
import {
  get_automation_retire_url,
  get_automation_retirement_preflight_url,
  get_automation_unretire_url,
} from '@/utils/urls.ts';
import {
  AutomationRetirementPreflight,
  AutomationRetirementResponse,
} from '@/types/automations.ts';

const retirementKey = (automationId: string) => [...AUTOMATION_KEY(automationId), 'retirement-preflight'];

export const useAutomationRetirementPreflight = (automationId: string, enabled: boolean) => {
  const apiClient = useApiClient();
  return useQuery<AutomationRetirementPreflight, AxiosError>({
    queryKey: retirementKey(automationId),
    queryFn: async () => (await apiClient.get(get_automation_retirement_preflight_url(automationId))).data,
    enabled,
  });
};

export const useAutomationRetirementMutation = (automationId: string) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<AutomationRetirementResponse, AxiosError<{ detail?: string }>, { retired: boolean }>({
    mutationFn: async ({ retired }) => {
      const url = retired ? get_automation_retire_url(automationId) : get_automation_unretire_url(automationId);
      return (await apiClient.post(url, { confirm: true })).data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: AUTOMATION_KEY(automationId) }),
        queryClient.invalidateQueries({ queryKey: AUTOMATION_BASE_KEY(automationId) }),
        queryClient.invalidateQueries({ queryKey: AUTOMATIONS_KEY }),
        queryClient.invalidateQueries({ queryKey: retirementKey(automationId) }),
      ]);
    },
  });
};
